// src/models/Domain.js
class Domain {
    constructor(data = {}) {
      this.id = data.id || data._id || null;
      this._id = data._id || data.id || null;
      this.domainId = data.domainId || `DOM${Date.now()}`;
      this.subdomain = data.subdomain || '';
      this.customDomain = data.customDomain || '';
      this.domainType = data.domainType || 'subdomain'; // subdomain or custom
      this.redirectPath = data.redirectPath || '/dashboard';
      this.allowedModules = data.allowedModules || [];
      this.sslEnabled = data.sslEnabled !== undefined ? data.sslEnabled : true;
      this.sslCertificate = data.sslCertificate || null;
      this.sslExpiryDate = data.sslExpiryDate || null;
      this.dnsVerified = data.dnsVerified || false;
      this.dnsRecords = data.dnsRecords || [];
      this.isActive = data.isActive !== undefined ? data.isActive : true;
      this.createdBy = data.createdBy || null;
      this.lastVerifiedAt = data.lastVerifiedAt || null;
      this.metadata = data.metadata || {};
      this.createdAt = data.createdAt || new Date().toISOString();
      this.updatedAt = data.updatedAt || new Date().toISOString();
    }
  
    getFullDomain() {
      const isLocalhost = typeof window !== 'undefined' && 
                         (window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname.includes('localhost'));
      
      const port = typeof window !== 'undefined' && window.location.port 
                   ? ':' + window.location.port 
                   : '';
      
      if (this.domainType === 'custom') {
        return this.customDomain;
      }
      
      if (isLocalhost) {
        return `${this.subdomain}.localhost${port}`;
      }
      
      return `${this.subdomain}.yourapp.com`;
    }
  
    getStatusColor() {
      if (!this.isActive) return '#64748b';
      if (!this.dnsVerified) return '#f59e0b';
      return '#10b981';
    }
  
    getStatusText() {
      if (!this.isActive) return 'Inactive';
      if (!this.dnsVerified) return 'Pending Verification';
      return 'Active';
    }
  
    getSSLStatusColor() {
      if (!this.sslEnabled) return '#64748b';
      if (this.isSSLExpiringSoon()) return '#f59e0b';
      if (this.sslCertificate) return '#10b981';
      return '#3b82f6';
    }
  
    getSSLStatusText() {
      if (!this.sslEnabled) return 'SSL Disabled';
      if (!this.sslCertificate) return 'SSL Pending';
      if (this.isSSLExpiringSoon()) return 'SSL Expiring Soon';
      return 'SSL Active';
    }
  
    isSSLExpiringSoon() {
      if (!this.sslExpiryDate) return false;
      const expiryDate = new Date(this.sslExpiryDate);
      const daysUntilExpiry = (expiryDate - new Date()) / (1000 * 60 * 60 * 24);
      return daysUntilExpiry < 30;
    }
  
    isVerified() {
      return this.dnsVerified;
    }
  
    needsVerification() {
      return !this.dnsVerified && this.isActive;
    }
  
    getDNSRecordsText() {
      if (!this.dnsRecords || this.dnsRecords.length === 0) {
        return 'No DNS records configured';
      }
      return this.dnsRecords.map(record => 
        `${record.type}: ${record.name} → ${record.value}`
      ).join('\n');
    }
  
    validate() {
      const errors = [];
      
      if (this.domainType === 'subdomain') {
        if (!this.subdomain?.trim()) {
          errors.push('Subdomain is required');
        } else if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(this.subdomain)) {
          errors.push('Subdomain can only contain lowercase letters, numbers, and hyphens');
        } else if (this.subdomain.length < 3) {
          errors.push('Subdomain must be at least 3 characters long');
        }
      } else if (this.domainType === 'custom') {
        if (!this.customDomain?.trim()) {
          errors.push('Custom domain is required');
        } else if (!/^[a-z0-9][a-z0-9-]*\.[a-z]{2,}$/.test(this.customDomain)) {
          errors.push('Invalid custom domain format');
        }
      } else {
        errors.push('Domain type must be either "subdomain" or "custom"');
      }
      
      if (!this.redirectPath?.trim()) {
        errors.push('Redirect path is required');
      } else if (!this.redirectPath.startsWith('/')) {
        errors.push('Redirect path must start with /');
      }
      
      return { 
        isValid: errors.length === 0, 
        errors 
      };
    }
  
    toJSON() {
      return {
        id: this.id,
        _id: this._id,
        domainId: this.domainId,
        subdomain: this.subdomain,
        customDomain: this.customDomain,
        domainType: this.domainType,
        redirectPath: this.redirectPath,
        allowedModules: this.allowedModules,
        sslEnabled: this.sslEnabled,
        sslCertificate: this.sslCertificate,
        sslExpiryDate: this.sslExpiryDate,
        dnsVerified: this.dnsVerified,
        dnsRecords: this.dnsRecords,
        isActive: this.isActive,
        createdBy: this.createdBy,
        lastVerifiedAt: this.lastVerifiedAt,
        metadata: this.metadata,
        createdAt: this.createdAt,
        updatedAt: this.updatedAt
      };
    }
  
    static fromJSON(json) {
      return new Domain(json);
    }
  }
  
  export default Domain;